/**
 * useScheduleSync — Real-time Firestore listener for Schedule Events & Scenes
 * 
 * Subscribes to scoped project rehearsalSchedule and rehearsalScenes
 * subcollections and pushes incoming data into the Zustand SchedulerSlice.
 * 
 * Guards: Only subscribes when cloud.enabled, cloud.orgId, cloud.projectId, and
 * auth.initialized are all truthy — same pattern as useFeedSync.
 */

import { useEffect, useRef } from 'react';
import { useAppStore } from '../../../store/useAppStore';
import { subscribeToScheduleEvents, subscribeToRehearsalScenes } from '../schedule-api';
import {
    clearPendingScheduleDeletesMissingFromRemote,
    clearPendingScheduleWrite,
    clearPendingScheduleWrites,
    isPendingScheduleDelete,
    isPendingScheduleWrite,
} from '../schedule-pending-writes';
import type { ScheduleEvent } from '../../scheduler/types';

const PENDING_LOCAL_EVENT_WINDOW_MS = 60_000;

function isRecentScheduleEvent(event: ScheduleEvent, nowMs: number): boolean {
    return typeof event.updatedAt === 'number'
        && nowMs - event.updatedAt >= 0
        && nowMs - event.updatedAt <= PENDING_LOCAL_EVENT_WINDOW_MS;
}

function normalizePendingComparisonValue(value: unknown): unknown {
    if (Array.isArray(value)) {
        return value.map(normalizePendingComparisonValue);
    }
    if (value && typeof value === 'object') {
        return Object.fromEntries(
            Object.entries(value as Record<string, unknown>)
                .filter(([, entryValue]) => entryValue !== undefined)
                .toSorted(([leftKey], [rightKey]) => leftKey.localeCompare(rightKey))
                .map(([key, entryValue]) => [key, normalizePendingComparisonValue(entryValue)])
        );
    }
    return value;
}

function scheduleEventsMatchForPendingWrite(left: ScheduleEvent, right: ScheduleEvent): boolean {
    return JSON.stringify(normalizePendingComparisonValue(left))
        === JSON.stringify(normalizePendingComparisonValue(right));
}

export function mergeScheduleEventsWithPendingLocal(
    remoteEvents: ScheduleEvent[],
    localEvents: ScheduleEvent[],
    nowMs = Date.now(),
): ScheduleEvent[] {
    const remoteIds = new Set(remoteEvents.map(event => event.id));
    const localById = new Map(localEvents.map(event => [event.id, event]));
    const visibleRemoteEvents = remoteEvents
        .filter(event => !isPendingScheduleDelete(event.id))
        .map(event => {
            const localEvent = localById.get(event.id);
            if (localEvent && isPendingScheduleWrite(event.id)) {
                return localEvent;
            }
            return event;
        });
    const pendingLocalEvents = localEvents.filter(event => (
        !remoteIds.has(event.id)
        && !isPendingScheduleDelete(event.id)
        && (isPendingScheduleWrite(event.id) || isRecentScheduleEvent(event, nowMs))
    ));

    return [...visibleRemoteEvents, ...pendingLocalEvents].toSorted((left, right) => (
        left.date.localeCompare(right.date)
        || left.startTime.localeCompare(right.startTime)
        || left.title.localeCompare(right.title)
    ));
}

export function useScheduleSync(enabled = true) {
    const cloudEnabled = useAppStore(state => state.cloud.enabled);
    const orgId = useAppStore(state => state.cloud.orgId);
    const projectId = useAppStore(state => state.cloud.projectId);
    const isAuthInitialized = useAppStore(state => state.auth.initialized);
    const user = useAppStore(state => state.auth.user);

    const eventUnsubRef = useRef<(() => void) | null>(null);
    const sceneUnsubRef = useRef<(() => void) | null>(null);
    const mountedRef = useRef(true);

    useEffect(() => {
        mountedRef.current = true;
        return () => { mountedRef.current = false; };
    }, []);

    useEffect(() => {
        // Tear down previous subscriptions on any dependency change
        if (eventUnsubRef.current) {
            eventUnsubRef.current();
            eventUnsubRef.current = null;
        }
        if (sceneUnsubRef.current) {
            sceneUnsubRef.current();
            sceneUnsubRef.current = null;
        }

        // Guard: Only subscribe when all conditions are met
        if (!enabled || !cloudEnabled || !orgId || !projectId || !isAuthInitialized || !user) {
            return;
        }

        const scope = { orgId, projectId };
        clearPendingScheduleWrites();

        // Subscribe to schedule events
        eventUnsubRef.current = subscribeToScheduleEvents(scope, (events) => {
            if (!mountedRef.current) return;
            const remoteIds = new Set(events.map(event => event.id));
            clearPendingScheduleDeletesMissingFromRemote(remoteIds);
            useAppStore.setState(state => ({
                rehearsalSchedule: mergeScheduleEventsWithPendingLocal(events, state.rehearsalSchedule || []),
            }));
            const localById = new Map((useAppStore.getState().rehearsalSchedule || []).map(event => [event.id, event]));
            for (const event of events) {
                const localEvent = localById.get(event.id);
                if (!localEvent || scheduleEventsMatchForPendingWrite(event, localEvent)) {
                    clearPendingScheduleWrite(event.id);
                }
            }
        });

        // Subscribe to rehearsal scenes
        sceneUnsubRef.current = subscribeToRehearsalScenes(scope, (scenes) => {
            if (!mountedRef.current) return;
            useAppStore.setState({ rehearsalScenes: scenes });
        });

        return () => {
            if (eventUnsubRef.current) {
                eventUnsubRef.current();
                eventUnsubRef.current = null;
            }
            if (sceneUnsubRef.current) {
                sceneUnsubRef.current();
                sceneUnsubRef.current = null;
            }
        };
    }, [enabled, cloudEnabled, orgId, projectId, isAuthInitialized, user]);
}
